import type {
	ApiCheckIamPermissionQueryParams,
	PermissionType,
} from '@repo/api-codegen';
import { useApiCheckIamPermission } from '@repo/api-codegen';
import type { EntityType } from '@repo/common/enums/entityType';
import { UserRole } from '@repo/common/enums/UserRole';
import type { IReport, ISecodaEntity, IUser } from '../../api';
import { useAuthUser } from '../../api';
import type { SecodaEntity } from '../../lib/models';
import { IS_PRODUCTION } from '../envs';

export const isProductionEnvironment = () =>
	IS_PRODUCTION && !window.location.hostname.includes('staging');

export const isViewer = (user: IUser) => {
	if (!user) return false;
	// TODO: Refactor this when user.role is a UserRole
	// instead of a string.
	if (UserRole.VIEWER.toString() === user.role.toLowerCase()) return true;
	if (UserRole.GUEST.toString() === user.role.toLowerCase()) return true;
	return false;
};

export const useV2CheckIAMPermission = ({
	v2Permission,
	entityId,
	entity,
	defaultResult = false,
}: {
	v2Permission?: PermissionType;
	entityId?: string;
	entity?: SecodaEntity | ISecodaEntity;
	defaultResult?: boolean;
}) => {
	const { workspace } = useAuthUser();
	const { data: checkResult } = useApiCheckIamPermission(
		{
			queryParams: {
				permission: v2Permission || 'Resources.Read',
				...(entityId || entity ? { object_id: entityId || entity?.id } : {}),
			},
		},
		{
			enabled:
				Boolean(workspace.migrated_permission_v2) && Boolean(v2Permission),
		}
	);

	return checkResult?.ok || defaultResult;
};

// Backward compatibility for old permission checks
export const useCheckIAMPermission = ({
	v1AllowedRoles,
	v2Permission,
	entityId,
	entity,
	objectType,
	defaultResult = false,
}: {
	v1AllowedRoles: Array<UserRole>;
	v2Permission: PermissionType;
	entityId?: string;
	entity?: SecodaEntity | ISecodaEntity;
	defaultResult?: boolean;
	objectType?: ApiCheckIamPermissionQueryParams['object_type'];
}): { hasPermission: boolean; isLoading: boolean } => {
	const { user, workspace } = useAuthUser();
	const { data: checkResult, isLoading } = useApiCheckIamPermission(
		{
			queryParams: {
				permission: v2Permission,
				...(entityId || entity ? { object_id: entityId || entity?.id } : {}),
				...(objectType ? { object_type: objectType } : {}),
			},
		},
		{
			enabled: Boolean(workspace?.migrated_permission_v2),
		}
	);

	if (workspace?.migrated_permission_v2) {
		const hasPermission = checkResult?.ok || defaultResult;
		return {
			hasPermission,
			isLoading,
		};
	}

	return {
		hasPermission: v1AllowedRoles.includes(user.role as UserRole),
		isLoading,
	};
};

export const useCheckEntityUpdatePermission = ({
	entity,
	objectType,
	v2Permission = 'Resources.Update',
}: {
	entity?: {
		id: string;
		entity_type?: string | EntityType;
		owners?: string[];
		current_user_permissions?: {
			write: boolean;
		};
	};
	objectType?: ApiCheckIamPermissionQueryParams['object_type'];
	v2Permission?: PermissionType;
}) => {
	const { workspace } = useAuthUser();
	const { hasPermission: checkIamResult } = useCheckIAMPermission({
		v1AllowedRoles: [UserRole.ADMIN, UserRole.EDITOR],
		v2Permission,
		entityId: entity?.id,
		objectType,
	});

	if (!entity) {
		return false;
	}

	if (!workspace.migrated_permission_v2 && entity.current_user_permissions) {
		return entity.current_user_permissions.write;
	}

	return checkIamResult;
};

export enum QuestionUpdatePermission {
	ALL_PROPERTIES = 'ALL_PROPERTIES',
	TITLE_AND_DEFINITION = 'TITLE_AND_DEFINITION',
}

export const useCheckQuestionUpdatePermission = ({
	entity,
}: {
	entity?: {
		id: string;
		entity_type?: string | EntityType;
		owners?: string[];
		current_user_permissions?: {
			write: boolean;
		};
	};
}) => {
	const { workspace } = useAuthUser();
	const { hasPermission: checkIamResult } = useCheckIAMPermission({
		v1AllowedRoles: [UserRole.ADMIN, UserRole.EDITOR],
		v2Permission: 'Questions.Update',
		entityId: entity?.id,
	});

	if (!entity) {
		return false;
	}

	if (!workspace.migrated_permission_v2 && entity.current_user_permissions) {
		return entity.current_user_permissions.write;
	}

	return checkIamResult;
};

export const coalesceName = (u?: IUser) => {
	const name = u?.display_name?.trim();

	if (!name) {
		return u?.email ?? 'User';
	}

	return name;
};

export const useCheckAnalyticsWritePermission = ({
	report,
}: {
	report?: IReport;
}) => {
	const { user } = useAuthUser();
	const { hasPermission: canEditWidgets } = useCheckIAMPermission({
		v1AllowedRoles: [UserRole.ADMIN],
		v2Permission: 'Analytics.Write',
	});

	return canEditWidgets || report?.owner?.id === user.id;
};
